/**
 * Start of a recursive ACL job (S8 Task 8.11): `hns-acl-recursive` with the root ETag read when the dialog opened. One
 * request at a time; closing the dialog cancels a start still in its preflight (main's request registry). The answer
 * is `{ started, jobId }` or a typed refusal (`TargetChanged`, authorization, …).
 */
import type { HnsPathRef, StorageAclChange, StorageOperationError } from '../../../../../types/azureStorage';
import { toStorageOperationError } from '../../services/azureStorageApi';
import { useSingleStorageRequest } from '../../properties/hooks/useSingleStorageRequest';
import { hnsAclRecursive } from '../hnsApi';

export type AclStartResult = { kind: 'started'; jobId: string } | { kind: 'failed'; error: StorageOperationError } | { kind: 'stale' };

export function useHnsAclRecursiveStart(connectionId: string) {
  const request = useSingleStorageRequest();
  const start = async (resource: HnsPathRef, change: StorageAclChange, expectedRootEtag: string): Promise<AclStartResult> => {
    const outcome = await request.run((requestId) => hnsAclRecursive({ connectionId, resource, change, expectedRootEtag, requestId }));
    if (!outcome.current) return { kind: 'stale' };
    if (outcome.error !== undefined) return { kind: 'failed', error: toStorageOperationError(outcome.error) };
    const answer = outcome.value;
    if (!answer) return { kind: 'stale' };
    if (answer.ok === false) return { kind: 'failed', error: answer.error };
    return answer.value.started ? { kind: 'started', jobId: answer.value.jobId } : { kind: 'stale' };
  };
  return { pending: request.pending, start, cancel: request.cancel };
}
